import { useCallback, useEffect, useMemo, useRef } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { AttributeStatus } from 'companySettings/enums';
import {
  getPartFinderProductTypeQueryKey,
  useGetPartFinderProductType,
  usePartFinderProductTypeActions,
} from 'companySettings/hooks';
import { IPartFinderProductType } from 'companySettings/types';
import { NotificationPosition } from 'core/enums';
import { FormProvider, useAgGridClientTableContext, useNotificationContext } from 'core/states';

import { mapPartFinderProductTypePayload } from './mappers';
import { upsertPartFinderProductTypeSchema } from './schema';
import { IUpsertPartFinderProductTypeFormData } from './types';
import { UpsertPartFinderProductTypeFormFields } from './UpsertPartFinderProductTypeFormFields';

interface IProps {
  productType?: IPartFinderProductType;
  isOpen: boolean;
  onClose: () => void;
}

export const UpsertPartFinderProductType = ({ productType: row, isOpen, onClose }: IProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { clearNotification, showLoadingNotification, showErrorNotification } =
    useNotificationContext();

  // The grid's row has no attributes, so an existing product type is loaded with them when its
  // editor opens, and the editor opens once it has them.
  const {
    data: loadedProductType,
    isLoading: isProductTypeLoading,
    isError: isProductTypeLoadFailed,
    isSuccess: isProductTypeLoaded,
  } = useGetPartFinderProductType(row?.id, isOpen);
  const productType = row ? loadedProductType ?? undefined : undefined;
  const isEditorOpen = isOpen && (!row || Boolean(productType));

  // Each opening loads the product type afresh rather than showing the copy from last time.
  const closeEditor = useCallback(() => {
    onClose();

    if (row?.id) {
      queryClient.removeQueries(getPartFinderProductTypeQueryKey(row.id));
    }
  }, [onClose, queryClient, row?.id]);

  const isShowingLoadNoticeRef = useRef(false);

  // While it loads, a notice says so; a load that fails, or finds the product type gone, says that
  // and closes, so the trigger works again and nothing opens on its own later.
  useEffect(() => {
    if (!isOpen || !row) {
      return;
    }

    if (isProductTypeLoading) {
      isShowingLoadNoticeRef.current = true;
      showLoadingNotification({ message: t('messageLoading') }, NotificationPosition.TOP);

      return;
    }

    if (isProductTypeLoadFailed || (isProductTypeLoaded && !loadedProductType)) {
      isShowingLoadNoticeRef.current = false;
      showErrorNotification({ title: t('errorTitleDataLoading') }, NotificationPosition.TOP);
      closeEditor();

      return;
    }

    // Only the notice this load put up goes, never one a save showed meanwhile.
    if (isShowingLoadNoticeRef.current) {
      isShowingLoadNoticeRef.current = false;
      clearNotification();
    }
  }, [
    clearNotification,
    closeEditor,
    isOpen,
    isProductTypeLoadFailed,
    isProductTypeLoaded,
    isProductTypeLoading,
    loadedProductType,
    row,
    showErrorNotification,
    showLoadingNotification,
    t,
  ]);
  const { updateRows } = useAgGridClientTableContext();

  const { savePartFinderProductType } = usePartFinderProductTypeActions();

  const defaultValues = useMemo(
    (): IUpsertPartFinderProductTypeFormData => ({
      attributes: productType?.attributes ?? [],
      name: productType?.name ?? '',
    }),
    [productType]
  );

  const handleOnClose = useCallback(
    (reset?: UseFormReset<IUpsertPartFinderProductTypeFormData>) => {
      clearNotification();
      reset?.(defaultValues);
      closeEditor();
    },
    [clearNotification, closeEditor, defaultValues]
  );

  const onSubmit = useCallback(
    (status: AttributeStatus) =>
      (reset: UseFormReset<IUpsertPartFinderProductTypeFormData>) =>
      async (data: IUpsertPartFinderProductTypeFormData) => {
        const { success, partFinderProductType: savedProductType } =
          await savePartFinderProductType(
            mapPartFinderProductTypePayload(data, status, productType)
          );

        if (!success) {
          return;
        }

        // The row changes only once the drawer goes: the drawer can sit in the row's own cell, the
        // selection bar or the empty state, and the update re-renders or removes each of them.
        setTimeout(() => {
          handleOnClose(reset);
          updateRows(row ? { update: [savedProductType] } : { add: [savedProductType] }, {
            refreshCells: true,
          });
        }, 1000);
      },
    [handleOnClose, productType, row, savePartFinderProductType, updateRows]
  );

  return (
    <FormProvider
      schema={upsertPartFinderProductTypeSchema()}
      defaultValues={defaultValues}
      resetDefaultValues
    >
      <UpsertPartFinderProductTypeFormFields
        productType={productType}
        isOpen={isEditorOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
